import { Box } from '@mui/material'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Context from '../Context'
import theme from '../theme'
import { useFeed, FONT } from '../components/board'
import { useBoardFilters } from '../components/BoardFilters'
import SelectionsTable from '../components/SelectionsTable'
import FixturesTable from '../components/FixturesTable'

/**
 * One board rather than two. The search board and the upcoming board were the same feed behind the same
 * filters, differing only in what a row stood for — so that is all this screen asks:
 *
 *   Games      — a row per fixture, markets across the row, the other books behind the arrow
 *   Selections — a row per selection, our price against Pinnacle's, and the gap between them
 *
 * Everything else — which sport, which leagues, which markets, how soon it kicks off — is the filter row
 * both boards already shared. Ladders live on the favourites board, where there is room for them.
 */

const SAVED = 'gamesDisplay'

const LAYOUTS = [
  { key: 'games', label: 'Games' },
  { key: 'selections', label: 'Selections' }
]

const chip = (active) => ({
  appearance: 'none',
  height: '26px',
  padding: '0 10px',
  border: `1px solid ${active ? theme.palette.primary.light : '#D6D6DC'}`,
  backgroundColor: active ? theme.palette.primary.light : 'white',
  color: active ? 'white' : '#6E7378',
  borderRadius: '2px',
  fontFamily: FONT,
  fontSize: '12px',
  cursor: 'pointer'
})

function GamesPage({ pollingInterval }) {
  const { settings, setSettings, setIsSideBarOpen } = useContext(Context)

  /**
   * The columns want the width, and the menu beside them is navigation nobody is using while reading a
   * board. It folds away on arrival and stays where the user puts it afterwards.
   */
  useEffect(() => {
    setIsSideBarOpen(false)
    // eslint-disable-next-line
  }, [])

  const saved = settings[SAVED] || {}
  // selections by default: the question this board is opened with is where we are out of line, and the
  // fixture rows are the answer to a different one
  const [ layout, setLayout ] = useState(LAYOUTS.some((one) => one.key === saved.layout) ? saved.layout : 'selections')

  const chosen = useMemo(() => ({ layout }), [layout])
  const touched = useRef(false)
  useEffect(() => {
    if (!touched.current || JSON.stringify(chosen) === JSON.stringify(settings[SAVED] || {})) {
      return undefined
    }
    const timer = setTimeout(() => setSettings((current) => ({ ...current, [SAVED]: chosen })), 400)
    return () => clearTimeout(timer)
    // eslint-disable-next-line
  }, [chosen])

  const edit = (apply) => (...args) => {
    touched.current = true
    apply(...args)
  }

  /**
   * One set of filters for the whole board, under the key the search board has always saved to, so
   * nobody's sports and leagues are lost to the merge. The gap belongs to the selections layout — it is
   * the only one with our own price on it — so the slider goes wherever the gap column does.
   */
  const selections = layout === 'selections'
  const shown = useRef(null)

  // the filters are chosen first, because the feed is now asked for exactly what they select
  const filters = useBoardFilters('searchFilters', shown.current, { gap: selections })
  const { events, error, token } = useFeed(pollingInterval, filters.query)

  /**
   * What the picker's star reads to know whether a team is already starred. The filters are built before
   * the feed answers, so the events are handed back rather than passed in — and written in an effect
   * rather than during the render, because a render can be thrown away and re-run.
   */
  useEffect(() => {
    shown.current = events
  }, [events])


  return (
    <Box sx={{ padding: '20px 24px', minWidth: 'fit-content' }}>
      <Box sx={{ backgroundColor: 'white', padding: '20px 22px', minWidth: 'fit-content' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          {LAYOUTS.map((one) => (
            <Box key={one.key} component="button" type="button" onClick={edit(() => setLayout(one.key))} sx={chip(layout === one.key)}>
              {one.label}
            </Box>
          ))}
        </Box>

        {filters.card}

        <Box sx={{ height: '14px' }} />

        {selections ? (
          <SelectionsTable events={events} error={error} filters={filters} pollingInterval={pollingInterval} feedToken={token} />
        ) : (
          <FixturesTable events={events} error={error} filters={filters} />
        )}
      </Box>

    </Box>
  )
}

export default GamesPage
